// The brand's labels (D-100): make, rename and delete them, and hashtag rules that label posts
// by themselves. On the Sources page.
import type { ActionResult } from "@brandwall/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { ApiError, del, patch, post } from "../api";
import { useLabels } from "./PostLabels";
import { useToast } from "./Toast";

export function LabelsCard({ canEdit }: { canEdit: boolean }) {
  const { ws } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const labels = useLabels();
  const [name, setName] = useState("");
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [rule, setRule] = useState({ hashtag: "", label: "" });
  const run = async (call: () => Promise<ActionResult>) => {
    try {
      toast((await call()).summary);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["labels", ws] }),
        qc.invalidateQueries({ queryKey: ["posts", ws] }),
      ]);
      return true;
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "That didn't work. Please try again.");
      return false;
    }
  };
  const list = labels.data?.labels ?? [];
  const rules = labels.data?.rules ?? [];
  const nameOf = (id: string) => list.find((l) => l.id === id)?.name ?? "";

  return (
    <section className="card stack">
      <div>
        <h2>Labels</h2>
        <p className="muted">
          Your own words for groups of posts, like Women, Men or Summer. Label posts in your Inbox
          and in Discover, and choose which labels a wall shows in its Settings. Brandwall's AI
          suggests labels for new posts for you to confirm.
        </p>
      </div>
      {list.length === 0 && <p className="small muted">No labels yet.</p>}
      {list.map((l) => (
        <div key={l.id} className="row" style={{ gap: 8 }}>
          {renaming?.id === l.id ? (
            <form
              className="row spacer"
              style={{ gap: 6 }}
              onSubmit={async (e) => {
                e.preventDefault();
                if (
                  renaming.name.trim() &&
                  (await run(() =>
                    patch<ActionResult>(`/v1/w/${ws}/labels/${l.id}`, {
                      name: renaming.name.trim(),
                    }),
                  ))
                )
                  setRenaming(null);
              }}
            >
              <input
                className="input"
                maxLength={40}
                aria-label="Label name"
                value={renaming.name}
                onChange={(e) => setRenaming({ id: l.id, name: e.target.value })}
              />
              <button type="submit" className="btn sm">
                Save
              </button>
              <button type="button" className="btn link sm" onClick={() => setRenaming(null)}>
                Cancel
              </button>
            </form>
          ) : (
            <span className="spacer">
              <b>{l.name}</b>{" "}
              <span className="small muted">
                {l.posts} post{l.posts === 1 ? "" : "s"}
                {l.suggested ? `, ${l.suggested} suggested` : ""} · for a page:{" "}
                <code>data-label="{l.slug}"</code>
              </span>
            </span>
          )}
          {canEdit && renaming?.id !== l.id && (
            <>
              <button
                type="button"
                className="btn link sm"
                onClick={() => setRenaming({ id: l.id, name: l.name })}
              >
                Rename
              </button>
              <button
                type="button"
                className="btn link sm"
                onClick={() => {
                  if (
                    window.confirm(
                      `Delete the label "${l.name}"? It comes off every post and wall.`,
                    )
                  )
                    void run(() => del<ActionResult>(`/v1/w/${ws}/labels/${l.id}`));
                }}
              >
                Delete
              </button>
            </>
          )}
        </div>
      ))}
      {canEdit && (
        <form
          className="row wrap"
          onSubmit={async (e) => {
            e.preventDefault();
            if (
              name.trim() &&
              (await run(() => post<ActionResult>(`/v1/w/${ws}/labels`, { name: name.trim() })))
            )
              setName("");
          }}
        >
          <input
            className="input"
            style={{ flex: "1 1 200px" }}
            maxLength={40}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Women"
            aria-label="New label"
          />
          <button type="submit" className="btn secondary" disabled={!name.trim()}>
            Add label
          </button>
        </form>
      )}

      <div className="stack" style={{ gap: 6 }}>
        <b>Label posts by hashtag</b>
        <span className="small muted">
          Posts whose caption has the hashtag get the label, now and when they come in.
        </span>
        {rules.map((r) => (
          <div key={r.id} className="row" style={{ gap: 8 }}>
            <span className="spacer small">
              Posts with <b>#{r.hashtag}</b> get <b>{nameOf(r.labelId)}</b>
            </span>
            {canEdit && (
              <button
                type="button"
                className="btn link sm"
                onClick={() =>
                  void run(() => del<ActionResult>(`/v1/w/${ws}/labels/rules/${r.id}`))
                }
              >
                Remove
              </button>
            )}
          </div>
        ))}
        {canEdit && (
          <form
            className="row wrap"
            onSubmit={async (e) => {
              e.preventDefault();
              const hashtag = rule.hashtag.replace(/^#/, "").trim().toLowerCase();
              if (
                hashtag &&
                rule.label.trim() &&
                (await run(() =>
                  post<ActionResult>(`/v1/w/${ws}/labels/rules`, {
                    hashtag,
                    label: rule.label.trim(),
                  }),
                ))
              )
                setRule({ hashtag: "", label: "" });
            }}
          >
            <input
              className="input"
              style={{ flex: "1 1 140px" }}
              placeholder="#summerride"
              aria-label="Hashtag"
              value={rule.hashtag}
              onChange={(e) => setRule({ ...rule, hashtag: e.target.value })}
            />
            <input
              className="input"
              style={{ flex: "1 1 140px" }}
              placeholder="Summer"
              aria-label="Label"
              maxLength={40}
              list="label-names"
              value={rule.label}
              onChange={(e) => setRule({ ...rule, label: e.target.value })}
            />
            <datalist id="label-names">
              {list.map((l) => (
                <option key={l.id} value={l.name} />
              ))}
            </datalist>
            <button
              type="submit"
              className="btn secondary"
              disabled={!rule.hashtag.trim() || !rule.label.trim()}
            >
              Add rule
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
